import React from 'react';
import { Tabs, Badge, Row, Layout, Typography } from 'antd';
import 'tailwindcss/tailwind.css';
import { LOCAL_AR, THEME_VARIABLE } from '@/config';
import useLanguageStore from '@/stores/useLanguageStore';
import { useTranslation } from 'react-i18next';
import { shortenNumber } from '@/utils';

const { TabPane } = Tabs;

export interface TabItem {
  key: string | number;
  title: string;
  content?: React.ReactNode;
  badge?: number;
}

export enum TabSizeEnum {
  SMALL = '12px',
  MEDIUM = '16px',
}

interface TabBarProps<T extends string = string> {
  tabItems: TabItem[];
  activeTab: T;
  setActiveTab: (value: T) => void;
  handleChange?: (activeKey: string) => void;
  size?: TabSizeEnum;
  noBorder?: boolean;
  badgeStyle?: any;
  overflowBadgeCount?: number;
}

const { Text } = Typography;

const TabBar = <T extends string = string>({
  tabItems,
  setActiveTab,
  activeTab,
  handleChange,
  size = TabSizeEnum.MEDIUM,
  noBorder = false,
  badgeStyle,
  overflowBadgeCount,
}: TabBarProps<T>) => {
  const { i18n } = useTranslation();

  return (
    <Layout className={`bg-white rounded-lg ${noBorder ? '' : 'shadow-card-dark'} overflow-hidden`}>
      <Tabs
        activeKey={activeTab}
        onChange={(activeKey: string) => {
          setActiveTab(activeKey as T);
          handleChange?.(activeKey);
        }}
        className='flex flex-col items-start'
        tabBarStyle={{ margin: 0, width: '100%' }}
        size={size === TabSizeEnum.MEDIUM ? 'middle' : 'small'}
        rootClassName='ant-tabs-nav-small-tab'
      >
        {tabItems.map((tab) => (
          <TabPane
            tab={
              <Row
                className={`items-center  ${size === TabSizeEnum.MEDIUM ? ' mx-3 ' : i18n.language === LOCAL_AR ? 'mx-2' : ' mx-[3px]'} flex  gap-1`}
              >
                <Text
                  className={`text-[${size}] 
                    ${size === TabSizeEnum.MEDIUM ? ' mx-2 font-medium' : ' font-normal'} 
                    ${activeTab === tab.key ? 'text-customTunaPrimary  font-medium' : 'text-customGrayShade5'}`}
                >
                  {tab.title}
                </Text>
                {tab.badge !== undefined ? (
                  <Badge
                    title={tab.badge.toString()}
                    count={overflowBadgeCount ? tab.badge : shortenNumber(tab.badge)}
                    overflowCount={overflowBadgeCount ?? Number.MAX_SAFE_INTEGER}
                    showZero={false}
                    style={
                      badgeStyle ?? {
                        backgroundColor: THEME_VARIABLE.customGrayShade2,
                        color: THEME_VARIABLE.customGrayShade5,
                        borderRadius: '6px',
                      }
                    }
                    size={size === TabSizeEnum.MEDIUM ? 'default' : 'small'}
                  />
                ) : null}
              </Row>
            }
            key={tab.key}
          >
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    </Layout>
  );
};

export default TabBar;
